/**
 * Размеры закреплённых слоёв экрана → CSS-переменные на <html> (25.09.2026,
 * найдено проверкой экранов тревоги в браузере).
 *
 * --alarm-stack-h — высота стека полос тревоги (alarm-bar.tsx). Стек закреплён
 * поверх экрана и закрывал верх экрана тревоги: заголовок плана здания, а при
 * городской тревоге (две полосы) — и саму инструкцию «Отойдите на 650 м на юг».
 * По переменной .viewer (styles.css) и закреплённые инструменты аварийной
 * карты (emergency-widgets.css) уходят ниже стека.
 *
 * --em-window-top — верх прозрачного окна героя тревоги на живую карту
 * (emergency-hero-parts.tsx). Шапка героя накрывала контролы карты: зум
 * просвечивал сквозь заголовок и не нажимался, ⓘ атрибуции (ODbL) — тоже.
 * По переменной ⓘ встаёт в угол окна (styles-maplibre.css).
 *
 * Слоя нет — переменной нет, у CSS запасное значение.
 */
export const ALARM_STACK_VAR = '--alarm-stack-h'
export const HERO_WINDOW_VAR = '--em-window-top'

/** Куда писать переменную — в браузере это document.documentElement.style. */
export interface VarTarget {
  setProperty(name: string, value: string): void
  removeProperty(name: string): string
}

/** Конструктор наблюдателя размеров (ResizeObserver; в тестах — подделка). */
export type SizeObserverCtor = new (
  onResize: () => void,
) => { observe(el: Element): void; disconnect(): void }

/**
 * Держать переменную `name` = read(el) px: записать сразу и обновлять, когда
 * меняется размер любого из `watch`. Возвращает отписку, которая убирает
 * переменную: слой ушёл — CSS берёт запасное значение.
 */
export function trackElementVar(
  el: HTMLElement,
  name: string,
  read: (el: HTMLElement) => number,
  target: VarTarget = document.documentElement.style,
  Observer: SizeObserverCtor | null = globalThis.ResizeObserver ?? null,
  watch: readonly Element[] = [el],
): () => void {
  const apply = (): void => target.setProperty(name, `${read(el)}px`)
  apply()
  // Без ResizeObserver (браузеры старше 2020 г.) — значение на момент показа.
  const observer = Observer ? new Observer(apply) : null
  for (const w of watch) observer?.observe(w)
  return () => {
    observer?.disconnect()
    target.removeProperty(name)
  }
}

/** Высота стека полос: пришла городская полоса, пропала «Включить звук». */
export function trackAlarmStackHeight(
  el: HTMLElement,
  target?: VarTarget,
  Observer?: SizeObserverCtor | null,
): () => void {
  return trackElementVar(el, ALARM_STACK_VAR, (e) => e.offsetHeight, target, Observer)
}

/**
 * Верх окна героя (от верха героя — он лежит в начале .viewer, как и карта).
 * Верх сдвигается, когда меняется шапка над окном (длинный заголовок, язык,
 * памятка), поэтому следим за всеми частями героя, а не только за окном.
 */
export function trackHeroWindowTop(
  el: HTMLElement,
  target?: VarTarget,
  Observer?: SizeObserverCtor | null,
): () => void {
  const parts = el.parentElement ? [...el.parentElement.children] : [el]
  return trackElementVar(el, HERO_WINDOW_VAR, (e) => e.offsetTop, target, Observer, parts)
}
